import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type ChatbotState = {
  isOpen: boolean;
  isProcessing: boolean;
  currentOrgId: string | undefined;
  unreadCounts: Record<string, number>;
  initialMessage?: string;
  isFullScreen: boolean;
  inputValue: string;
  hadUnreadOnOpen: boolean;
  open: (orgId: string, initialMessage?: string) => void;
  close: () => void;
  clearInitialMessage: () => void;
  setIsProcessing: (value: boolean) => void;
  incrementUnread: () => void;
  toggleFullScreen: () => void;
  setInputValue: (value: string) => void;
  clearHadUnreadOnOpen: () => void;
};

export const useChatbotState = create<ChatbotState>()(
  persist(
    (set) => ({
      isOpen: false,
      isProcessing: false,
      currentOrgId: undefined,
      unreadCounts: {},
      initialMessage: undefined,
      isFullScreen: false,
      inputValue: '',
      hadUnreadOnOpen: false,
      open: (orgId: string, initialMessage?: string) =>
        set((s) => ({
          isOpen: true,
          initialMessage,
          currentOrgId: orgId,
          hadUnreadOnOpen: (s.unreadCounts[orgId] ?? 0) > 0,
          unreadCounts: { ...s.unreadCounts, [orgId]: 0 },
        })),
      close: () => set({ isOpen: false, initialMessage: undefined }),
      clearInitialMessage: () => set({ initialMessage: undefined }),
      setIsProcessing: (value: boolean) => set({ isProcessing: value }),
      incrementUnread: () =>
        set((s) => {
          const orgId = s.currentOrgId;
          if (!orgId || s.isOpen) return {};
          return {
            unreadCounts: {
              ...s.unreadCounts,
              [orgId]: (s.unreadCounts[orgId] ?? 0) + 1,
            },
          };
        }),
      toggleFullScreen: () => set((s) => ({ isFullScreen: !s.isFullScreen })),
      setInputValue: (value: string) => set({ inputValue: value }),
      clearHadUnreadOnOpen: () => set({ hadUnreadOnOpen: false }),
    }),
    {
      name: 'chatbot-state',
      partialize: (state) => ({ unreadCounts: state.unreadCounts }),
    }
  )
);

export const useUnreadCount = (orgId: string | undefined) =>
  useChatbotState((s) => (orgId ? s.unreadCounts[orgId] ?? 0 : 0));
